import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileCheck, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import PortalLayout from './PortalLayout';
// invoice_date / quote_date / due_date / payment_date are INSTANTS (api#838)
// and render as the TENANT's day via formatInstantTenantDay — their UTC
// prefix read a day late for anything written after 18:00 MDT, on the
// screen the CUSTOMER reads. valid_until is a calendar day and stays on
// formatCalendarDate.
//
// Before that they were treated as calendar days: `new Date()` read them as
// UTC midnight and this portal showed every one of them a day early. The local
// fmtDate that fixed that then ended in a bare toLocaleDateString(), so the
// FORMAT tracked the reader's own device instead: one customer saw 3/23/2026
// and another 23/03/2026 for the same day, with nothing on the page to say
// which. formatCalendarDate's default renders a month NAME, which no device
// can reorder into a different day.
import { formatCalendarDate, formatInstantTenantDay } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency } from '../../lib/display-labels';

export default function PortalQuotes() {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();

  const { data: quotes, isLoading } = useQuery({
    queryKey: ['portal-quotes'],
    queryFn: () => api.portal.quotes().then(r => r.data.data),
  });

  // Money on a document the CUSTOMER reads. `parseFloat(n || '0')` turned an
  // amount the server did not send into $0.00 — an invoice total that is
  // genuinely unknown is not an invoice for nothing. @see fmtCurrency.
  const fmt = (n: number | string | null | undefined) => fmtCurrency(n, 'CAD');
  return (
    <PortalLayout>
      <div className="space-y-4 animate-fade-in">
        <h2 className="text-white font-bold text-lg">Quotes</h2>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={28} className="animate-spin text-fieldiq-accent" />
          </div>
        ) : quotes?.length === 0 ? (
          <div className="text-center py-16">
            <FileCheck size={40} className="mx-auto text-fieldiq-muted mb-3" />
            <p className="text-fieldiq-muted">No quotes found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {quotes?.map((q: any) => (
              <button
                key={q.id}
                onClick={() => navigate(`/portal/quotes/${q.id}`)}
                className="w-full bg-fieldiq-card rounded-xl border border-fieldiq-border p-4 flex items-center justify-between hover:border-fieldiq-accent/30 transition-colors"
              >
                <div className="text-left">
                  <p className="text-white font-medium text-sm">{q.quote_number}</p>
                  <p className="text-fieldiq-muted text-xs mt-0.5">
                    {(formatInstantTenantDay(q.quote_date, timeZone) ?? '—')} {q.valid_until && `· Valid until ${(formatCalendarDate(q.valid_until) ?? '—')}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-white font-semibold text-sm">{fmt(q.total_amount)}</p>
                  <StatusBadge status={q.status} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    sent: 'bg-blue-500/20 text-blue-400',
    accepted: 'bg-green-500/20 text-green-400',
    declined: 'bg-red-500/20 text-red-400',
  };
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded mt-1 inline-block ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status?.toUpperCase()}
    </span>
  );
}
